/**
 * Bill Validation — the nightly bill run's history (migration 118).
 *
 * Types and pure helpers for the Bulk Bill Run tab. Counts are computed from the
 * item rows the viewer can read (RLS: their org only), never stored on the run —
 * the job spans every organisation, so a stored count would be everyone's.
 */

export type BillRunStatus = 'running' | 'done' | 'partial' | 'failed' | 'empty';
export type BillRunOutcome = 'done' | 'error' | 'blocked';

export type BvBillRun = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: BillRunStatus;
  error: string | null;
};

export type BvBillRunItem = {
  id: string;
  run_id: string;
  bill_id: string;
  org_id: string;
  site_id: string | null;
  outcome: BillRunOutcome;
  reason: string | null;
  is_rerun: boolean;
  shadow_total: number | null;
  warnings: string[];
  report_created: boolean;
  created_at: string;
  bill?: {
    bill_from: string;
    bill_to: string;
    site?: { nmi: string | null; client?: { name: string | null } | null } | null;
  } | null;
};

export type RunOutcomeCounts = {
  total: number;
  done: number;
  error: number;
  blocked: number;
  reports: number;
};

export function runOutcomeCounts(
  items: Pick<BvBillRunItem, 'outcome' | 'report_created'>[],
): RunOutcomeCounts {
  const counts: RunOutcomeCounts = { total: 0, done: 0, error: 0, blocked: 0, reports: 0 };
  for (const item of items) {
    counts.total++;
    counts[item.outcome]++;
    if (item.report_created) counts.reports++;
  }
  return counts;
}

export function runDurationLabel(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return '—';
  const secs = Math.max(0, Math.round((Date.parse(finishedAt) - Date.parse(startedAt)) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/**
 * How the run went for THIS org. The run row's status is global — an org whose
 * bills all priced should not see "Some failed" because another org's did.
 * With nothing of this org's in the run (it failed before recording any bill,
 * say), the run's own status is all there is to show.
 */
export function orgRunStatus(run: Pick<BvBillRun, 'status'>, counts: RunOutcomeCounts): BillRunStatus {
  if (run.status === 'running') return 'running';
  if (counts.total === 0) return run.status;
  if (counts.error > 0) return counts.done > 0 ? 'partial' : 'failed';
  if (counts.done > 0) return 'done';
  return 'empty';
}
